import React, { createContext, useCallback, useContext, useState } from "react";
import { ToasterMessage } from "./Toaster";
import ToasterContainer from "./ToasterContainer";

// Simple UUID generator
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

interface ToasterContextType {
  showToaster: (
    message: string,
    options?: {
      type?: "info" | "success" | "warning" | "error";
      errorId?: string;
      duration?: number;
    },
  ) => string;
  removeToaster: (id: string) => void;
  clearAllToasters: () => void;
}

const ToasterContext = createContext<ToasterContextType | undefined>(undefined);

interface ToasterProviderProps {
  children: React.ReactNode;
  maxVisible?: number;
}

export const ToasterProvider: React.FC<ToasterProviderProps> = ({
  children,
  maxVisible = 3,
}) => {
  const [toasters, setToasters] = useState<ToasterMessage[]>([]);

  const showToaster = useCallback(
    (
      message: string,
      options?: {
        type?: "info" | "success" | "warning" | "error";
        errorId?: string;
        duration?: number;
      },
    ): string => {
      const id = generateId();
      const newToaster: ToasterMessage = {
        id,
        message,
        type: options?.type || "info",
        errorId: options?.errorId,
        duration: options?.duration,
      };

      setToasters((prev) => [...prev, newToaster]);
      return id;
    },
    [],
  );

  const removeToaster = useCallback((id: string) => {
    setToasters((prev) => prev.filter((toaster) => toaster.id !== id));
  }, []);

  const clearAllToasters = useCallback(() => {
    setToasters([]);
  }, []);

  const contextValue: ToasterContextType = {
    showToaster,
    removeToaster,
    clearAllToasters,
  };

  return (
    <ToasterContext.Provider value={contextValue}>
      {children}
      <ToasterContainer
        toasters={toasters}
        onRemove={removeToaster}
        maxVisible={maxVisible}
      />
    </ToasterContext.Provider>
  );
};

export const useToaster = (): ToasterContextType => {
  const context = useContext(ToasterContext);
  if (!context) {
    throw new Error("useToaster must be used within a ToasterProvider");
  }
  return context;
};

export default ToasterProvider;
